/**
 * Flattens a decoded buffer to the mono samples the analyser wants.
 *
 * The only browser-facing part of offline analysis. Everything downstream —
 * windowing, the FFT, the band split, onset detection — is pure arithmetic on
 * this array, which is why the analysis can run in a test with no audio stack
 * at all and produce the same numbers it produces here.
 *
 * Channels are averaged rather than taking the left one. A stereo mix with a
 * hard-panned element would otherwise analyse as though that element were half
 * as loud, or absent.
 */
export function decodeToMono(buffer: AudioBuffer): Float32Array {
  const channels = buffer.numberOfChannels;
  const length = buffer.length;
  if (channels === 0 || length === 0) return new Float32Array(0);

  const mono = new Float32Array(length);
  buffer.copyFromChannel(mono, 0);
  if (channels === 1) return mono;

  const scratch = new Float32Array(length);
  for (let channel = 1; channel < channels; channel += 1) {
    buffer.copyFromChannel(scratch, channel);
    for (let index = 0; index < length; index += 1) {
      mono[index] = (mono[index] as number) + (scratch[index] as number);
    }
  }
  const scale = 1 / channels;
  for (let index = 0; index < length; index += 1) {
    mono[index] = (mono[index] as number) * scale;
  }
  return mono;
}
